import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatCurrency, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import EmptyState from "../components/Help/EmptyState";
import {
  Building, Plus, Trash2, Edit3, Calendar, DollarSign,
  TrendingDown, ShieldCheck, FileText, Info, X, CheckCircle2,
  Layers, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const IMPROVEMENT_TYPES = [
  "Roof",
  "HVAC / Mechanical",
  "Plumbing",
  "Electrical",
  "Flooring",
  "Kitchen / Bath Remodel",
  "Structural / Foundation",
  "Addition / Expansion",
  "Appliances / Fixtures",
  "Land Improvements / Driveway",
  "Other",
];

const USEFUL_LIFE_PRESETS = [
  { label: "27.5 Years — Residential Rental Real Property (MACRS Straight Line)", value: 27.5 },
  { label: "15 Years — Land Improvements (Driveways, Fences, Patios)", value: 15.0 },
  { label: "5 Years — Appliances, Carpet, Furniture (5-Yr MACRS)", value: 5.0 },
  { label: "7 Years — Office Furniture & Equipment", value: 7.0 },
  { label: "39 Years — Non-Residential Commercial Property", value: 39.0 },
];

const EMPTY_IMPROVEMENT = {
  property_id: "",
  description: "",
  improvement_type: "Roof",
  date_placed_in_service: new Date().toISOString().slice(0, 10),
  amount: "",
  useful_life_years: 27.5,
  notes: "",
};

export default function CapitalImprovements() {
  const [improvements, setImprovements] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const [showModal, setShowModal] = useState(false);
  const [selectedCi, setSelectedCi] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_IMPROVEMENT);
  const [saving, setSaving] = useState(false);

  const [params, setParams] = useSearchParams();
  const { can } = useAuth();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [ciRes, propRes] = await Promise.all([
        api.get("/capital-improvements"),
        api.get("/properties"),
      ]);
      setImprovements(ciRes.data || []);
      setProperties(propRes.data || []);
    } catch (err) {
      toast.error("Failed to load capital improvements");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (params.get("new")) {
      setEditingItem(null);
      setForm(EMPTY_IMPROVEMENT);
      setShowModal(true);
      setParams({});
    }
  }, [params, setParams]);

  const handleOpenAdd = () => {
    setEditingItem(null);
    setForm(EMPTY_IMPROVEMENT);
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setForm({
      property_id: item.property_id,
      description: item.description,
      improvement_type: item.improvement_type,
      date_placed_in_service: item.date_placed_in_service,
      amount: item.amount.toString(),
      useful_life_years: item.useful_life_years,
      notes: item.notes || "",
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.property_id) {
      toast.error("Please select a property");
      return;
    }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      toast.error("Please enter a valid cost amount");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        property_id: form.property_id,
        description: form.description.trim(),
        improvement_type: form.improvement_type,
        date_placed_in_service: form.date_placed_in_service,
        amount: amt,
        useful_life_years: parseFloat(form.useful_life_years) || 27.5,
        notes: form.notes || null,
      };

      if (editingItem) {
        await api.patch(`/capital-improvements/${editingItem.id}`, payload);
        toast.success("Capital improvement updated");
      } else {
        await api.post("/capital-improvements", payload);
        toast.success("Capital improvement recorded");
      }
      setShowModal(false);
      setEditingItem(null);
      setForm(EMPTY_IMPROVEMENT);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save capital improvement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this capital improvement record?")) return;
    try {
      await api.delete(`/capital-improvements/${id}`);
      toast.success("Capital improvement removed");
      loadData();
    } catch (err) {
      toast.error("Failed to delete record");
    }
  };

  // Filter items
  const filteredItems = improvements.filter((item) => {
    if (propertyFilter !== "all" && item.property_id !== propertyFilter) return false;
    if (typeFilter !== "all" && item.improvement_type !== typeFilter) return false;
    return true;
  });

  // Calculate portfolio totals
  const totalCost = improvements.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
  const totalDepToDate = improvements.reduce((sum, i) => sum + (parseFloat(i.total_depreciation_to_date) || 0), 0);
  const totalRemainingBasis = improvements.reduce((sum, i) => sum + (parseFloat(i.remaining_basis) || 0), 0);
  const totalAnnualDep = improvements.reduce((sum, i) => sum + (parseFloat(i.annual_depreciation) || 0), 0);

  return (
    <Layout
      title="Capital Improvements Tracker"
      subtitle="IRS Form 4562 & MACRS Depreciation Tracking across your property portfolio"
      actions={
        can.edit && (
          <button
            onClick={handleOpenAdd}
            className="btn-primary text-xs sm:text-sm py-1.5 sm:py-2 px-3 sm:px-4 flex items-center gap-1.5"
          >
            <Plus size={15} /> Add Improvement
          </button>
        )
      }
    >
      {/* Portfolio Summary Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="card p-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-[var(--muted)] font-medium">Total Capital Invested</div>
            <div className="text-2xl font-black text-[var(--text)] mt-1">
              {formatCurrency(totalCost)}
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-0.5">Original unadjusted basis</div>
          </div>
          <div className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
            <DollarSign className="w-5 h-5" />
          </div>
        </div>

        <div className="card p-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-[var(--muted)] font-medium">Cumulative Depreciation</div>
            <div className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">
              {formatCurrency(totalDepToDate)}
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-0.5">Claimed to date</div>
          </div>
          <div className="p-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400">
            <TrendingDown className="w-5 h-5" />
          </div>
        </div>

        <div className="card p-4 flex items-center justify-between border-emerald-500/20 bg-emerald-50/20 dark:bg-emerald-950/10">
          <div>
            <div className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">Remaining Asset Basis</div>
            <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
              {formatCurrency(totalRemainingBasis)}
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-0.5">Unrecovered cost</div>
          </div>
          <div className="p-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400">
            <ShieldCheck className="w-5 h-5" />
          </div>
        </div>

        <div className="card p-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-[var(--muted)] font-medium">Annual Tax Depreciation</div>
            <div className="text-2xl font-black text-blue-600 dark:text-blue-400 mt-1">
              {formatCurrency(totalAnnualDep)}
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-0.5">Projected Form 4562 writeoff</div>
          </div>
          <div className="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400">
            <Building className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Guidance Alert Banner */}
      <div className="p-4 mb-6 rounded-2xl bg-blue-50/70 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900/40 text-xs text-slate-700 dark:text-slate-300 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <span className="font-bold text-slate-900 dark:text-slate-100">
            IRS Capital Improvement vs. Repair Rule:
          </span>
          <p className="leading-relaxed text-[11.5px]">
            Unlike routine repairs (which are 100% expensed on Schedule E in the current year), major capital improvements
            that add permanent value, prolong property life, or adapt it to a new use must be capitalized and depreciated over 27.5 years (IRC § 168).
            These records are automatically included in Section 5 of your CPA-Ready Schedule E Tax Package.
          </p>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            className="lapen-input text-xs py-1.5 px-3 w-auto"
          >
            <option value="all">All Properties ({properties.length})</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="lapen-input text-xs py-1.5 px-3 w-auto"
          >
            <option value="all">All Improvement Types</option>
            {IMPROVEMENT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div className="text-xs text-[var(--muted)] font-medium">
          Showing {filteredItems.length} of {improvements.length} improvements
        </div>
      </div>

      {/* Improvements Table */}
      <div className="card overflow-hidden p-0">
        {filteredItems.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={Building}
              title="No Capital Improvements Logged"
              description="Record major structural replacements, new roofs, HVAC installations, or remodels to calculate depreciation for your CPA."
              helpTopic="cat-repairs"
              helpText="Learn about Repairs vs. Capital Improvements"
              actionLabel="+ Add Capital Improvement"
              onAction={handleOpenAdd}
            />
          </div>
        ) : (
          <div className="table-responsive-container">
            {/* Desktop Table View (>= 700px) */}
            <div className="table-desktop-view overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-[var(--border)] bg-[var(--surface-hover)] text-[var(--muted)] font-bold uppercase tracking-wider text-[10.5px]">
                    <th className="p-3.5 pl-4">Description & Type</th>
                    <th className="p-3.5">Property</th>
                    <th className="p-3.5">Placed in Service</th>
                    <th className="p-3.5 text-right">Original Cost</th>
                    <th className="p-3.5 text-center">Useful Life</th>
                    <th className="p-3.5 text-right">Annual Depr.</th>
                    <th className="p-3.5 text-right">Depr. to Date</th>
                    <th className="p-3.5 text-right">Remaining Basis</th>
                    <th className="p-3.5 pr-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {filteredItems.map((ci) => (
                    <tr
                      key={ci.id}
                      onClick={() => setSelectedCi(ci)}
                      className="hover:bg-[var(--surface-hover)]/70 transition-colors cursor-pointer group"
                      data-testid={`ci-row-${ci.id}`}
                    >
                      <td className="p-3.5 pl-4">
                        <div className="font-bold text-sm text-[var(--text)] group-hover:text-teal-400 transition-colors">
                          {ci.description}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                            {ci.improvement_type}
                          </span>
                          {ci.notes && (
                            <span className="text-[11px] text-[var(--muted)] truncate max-w-xs" title={ci.notes}>
                              · {ci.notes}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3.5 font-medium text-[var(--text)]">
                        {ci.property_name || "—"}
                      </td>
                      <td className="p-3.5 text-[var(--muted)] whitespace-nowrap">
                        {formatDate(ci.date_placed_in_service)}
                      </td>
                      <td className="p-3.5 text-right font-bold text-[var(--text)] tabular-nums font-mono text-sm">
                        {formatCurrency(ci.amount)}
                      </td>
                      <td className="p-3.5 text-center text-[var(--muted)] whitespace-nowrap">
                        {ci.useful_life_years} yrs
                      </td>
                      <td className="p-3.5 text-right font-medium text-blue-600 dark:text-blue-400 tabular-nums font-mono">
                        {formatCurrency(ci.annual_depreciation)}/yr
                      </td>
                      <td className="p-3.5 text-right font-medium text-amber-600 dark:text-amber-400 tabular-nums font-mono">
                        {formatCurrency(ci.total_depreciation_to_date)}
                      </td>
                      <td className="p-3.5 text-right font-bold text-emerald-600 dark:text-emerald-400 tabular-nums font-mono">
                        {formatCurrency(ci.remaining_basis)}
                      </td>
                      <td className="p-3.5 pr-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          {can.edit && (
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(ci)}
                              className="p-1.5 rounded-lg text-[var(--muted)] hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                              title="Edit Record"
                            >
                              <Edit3 size={14} />
                            </button>
                          )}
                          {can.delete && (
                            <button
                              type="button"
                              onClick={() => handleDelete(ci.id)}
                              className="p-1.5 rounded-lg text-[var(--muted)] hover:text-red-400 hover:bg-red-500/10 transition-colors"
                              title="Delete Record"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards View (< 700px) according to "The Six Moves" */}
            <div className="table-cards-view divide-y divide-[var(--border)]">
              {filteredItems.map((ci) => (
                <div
                  key={ci.id}
                  onClick={() => setSelectedCi(ci)}
                  className="thumb-row hover:bg-[var(--surface-hover)] active:bg-[var(--surface-hover)] transition-colors group cursor-pointer select-none"
                  data-testid={`ci-card-${ci.id}`}
                  role="button"
                  tabIndex={0}
                >
                  {/* Line 1: Identity (Left) & Value (Top Right Slot) */}
                  <div className="flex items-baseline justify-between gap-3 min-w-0">
                    <span className="font-bold text-[15px] text-[var(--text)] group-hover:text-teal-400 transition-colors truncate min-w-0">
                      {ci.description}
                    </span>
                    {/* Move 3: ONE SLOT FOR THE AMOUNT - Top right, tabular figures */}
                    <span className="card-amount-slot text-[16px] text-teal-400">
                      {formatCurrency(ci.amount)}
                    </span>
                  </div>

                  {/* Line 2: Property & Type (Left) & Labeled Date (Right) */}
                  <div className="flex items-center justify-between gap-3 text-xs mt-1.5 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0 truncate">
                      <span className="text-[var(--text)] truncate font-medium">
                        {ci.property_name || "Portfolio"}
                      </span>
                      <span className="text-[var(--border)]">·</span>
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 shrink-0">
                        {ci.improvement_type}
                      </span>
                    </div>

                    {/* Move 4: Label the ambiguous ("In service 2024" instead of bare date) */}
                    <div className="text-[var(--muted)] font-medium shrink-0 whitespace-nowrap text-[11px]">
                      In service {formatDate(ci.date_placed_in_service)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Move 5: PROGRESSIVE DISCLOSURE VIA BOTTOM SHEET FOR CAPITAL ASSETS */}
      {selectedCi && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-150"
          onClick={() => setSelectedCi(null)}
          data-testid="ci-detail-modal"
        >
          <div
            className="card w-full max-w-lg p-0 overflow-hidden shadow-2xl rounded-t-3xl sm:rounded-3xl max-h-[90vh] flex flex-col my-0 sm:my-auto animate-in slide-in-from-bottom-4 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile Drawer Pull Handle */}
            <div className="sm:hidden pt-3 pb-1 flex justify-center">
              <div className="w-12 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700" />
            </div>

            {/* Header */}
            <div className="p-5 border-b border-[var(--border)] flex items-start justify-between bg-[var(--surface-hover)]">
              <div className="min-w-0 pr-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-teal-400">
                    Capital Asset Detail
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                    {selectedCi.improvement_type}
                  </span>
                </div>
                <h3 className="text-xl font-extrabold text-[var(--text)] mt-1 truncate">
                  {selectedCi.description}
                </h3>
                <p className="text-xs text-[var(--muted)] mt-0.5 truncate">
                  Property: {selectedCi.property_name || "Portfolio"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedCi(null)}
                className="p-1.5 rounded-xl text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface)] transition-colors shrink-0"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4 overflow-y-auto">
              {/* Cost & Remaining Basis Hero */}
              <div className="p-4 rounded-2xl bg-[var(--surface)] border border-[var(--border)] grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-[var(--muted)] font-medium">Original Cost Basis</div>
                  <div className="text-xl font-black text-[var(--text)] tabular-nums font-mono mt-0.5">
                    {formatCurrency(selectedCi.amount)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[var(--muted)] font-medium">Remaining Basis</div>
                  <div className="text-xl font-black text-emerald-400 tabular-nums font-mono mt-0.5">
                    {formatCurrency(selectedCi.remaining_basis)}
                  </div>
                </div>
              </div>

              {/* Depreciation Schedule Grid */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-3.5 rounded-xl bg-[var(--surface)] border border-[var(--border)]">
                  <span className="text-[var(--muted)] block text-[11px]">Placed in Service</span>
                  <strong className="text-sm text-[var(--text)] block mt-0.5">
                    {formatDate(selectedCi.date_placed_in_service)}
                  </strong>
                </div>

                <div className="p-3.5 rounded-xl bg-[var(--surface)] border border-[var(--border)]">
                  <span className="text-[var(--muted)] block text-[11px]">Recovery Useful Life</span>
                  <strong className="text-sm text-[var(--text)] block mt-0.5">
                    {selectedCi.useful_life_years} Years (MACRS)
                  </strong>
                </div>

                <div className="p-3.5 rounded-xl bg-[var(--surface)] border border-[var(--border)]">
                  <span className="text-[var(--muted)] block text-[11px]">Annual Depreciation</span>
                  <strong className="text-sm text-blue-400 block mt-0.5 tabular-nums font-mono">
                    {formatCurrency(selectedCi.annual_depreciation)}/yr
                  </strong>
                </div>

                <div className="p-3.5 rounded-xl bg-[var(--surface)] border border-[var(--border)]">
                  <span className="text-[var(--muted)] block text-[11px]">Total Depreciated to Date</span>
                  <strong className="text-sm text-amber-400 block mt-0.5 tabular-nums font-mono">
                    {formatCurrency(selectedCi.total_depreciation_to_date)}
                  </strong>
                </div>

                {selectedCi.notes && (
                  <div className="p-3.5 rounded-xl bg-[var(--surface)] border border-[var(--border)] col-span-2">
                    <span className="text-[var(--muted)] block text-[11px]">CPA & Depreciation Notes</span>
                    <p className="text-xs text-[var(--text)] mt-1 whitespace-pre-wrap">{selectedCi.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer with Actions */}
            <div className="p-4 border-t border-[var(--border)] bg-[var(--surface-hover)] flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setSelectedCi(null)}
                className="btn-secondary text-xs py-2 px-4"
              >
                Close
              </button>

              <div className="flex items-center gap-2">
                {can.delete && (
                  <button
                    type="button"
                    onClick={() => {
                      const id = selectedCi.id;
                      setSelectedCi(null);
                      handleDelete(id);
                    }}
                    className="p-2 text-xs rounded-xl text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1 font-medium"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                )}
                {can.edit && (
                  <button
                    type="button"
                    onClick={() => {
                      const item = selectedCi;
                      setSelectedCi(null);
                      handleOpenEdit(item);
                    }}
                    className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5"
                  >
                    <Edit3 size={14} /> Edit Record
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="card w-full max-w-lg p-6 shadow-2xl rounded-3xl space-y-4">
            <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-indigo-500/15 text-indigo-400 flex items-center justify-center">
                  <Building size={18} />
                </div>
                <h3 className="font-bold text-base text-[var(--text)]">
                  {editingItem ? "Edit Capital Improvement" : "Add Capital Improvement"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-xl text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)]"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              <div>
                <label className="label">Property *</label>
                <select
                  value={form.property_id}
                  onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                  className="lapen-input"
                  required
                >
                  <option value="">— Select Rental Property —</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.address})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Improvement Description *</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="e.g. Architectural shingle roof replacement, Trane 4-ton Heat Pump"
                  className="lapen-input"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">Improvement Type</label>
                  <select
                    value={form.improvement_type}
                    onChange={(e) => setForm({ ...form, improvement_type: e.target.value })}
                    className="lapen-input"
                  >
                    {IMPROVEMENT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label">Date Placed in Service *</label>
                  <input
                    type="date"
                    value={form.date_placed_in_service}
                    onChange={(e) => setForm({ ...form, date_placed_in_service: e.target.value })}
                    className="lapen-input"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">Total Cost / Amount ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                    className="lapen-input font-bold"
                    required
                  />
                </div>

                <div>
                  <label className="label">Recovery Period (Years)</label>
                  <select
                    value={form.useful_life_years}
                    onChange={(e) => setForm({ ...form, useful_life_years: parseFloat(e.target.value) })}
                    className="lapen-input"
                  >
                    {USEFUL_LIFE_PRESETS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Notes / Contractor Info</label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Contractor name, permit numbers, warranty info"
                  className="lapen-input"
                />
              </div>

              <div className="p-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-[11px] text-[var(--muted)] leading-relaxed">
                <strong>Tax Note:</strong> Residential real property improvements are depreciated straight-line over 27.5 years under MACRS.
                {parseFloat(form.amount) > 0 && (
                  <span className="block mt-1 text-slate-800 dark:text-slate-200">
                    Annual deduction: <strong>${((parseFloat(form.amount) || 0) / (parseFloat(form.useful_life_years) || 27.5)).toFixed(2)}/year</strong>
                  </span>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t border-[var(--border)]">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-1.5"
                >
                  <CheckCircle2 size={15} />
                  <span>{editingItem ? "Update Record" : "Save Record"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
